import type { CriteriaGroup, CriteriaSelection } from "./components/CriteriaFilter";
import type { RecommendationRow } from "./types";

// Backend criteria look like "Segment | Country or port | Revenue band"; segment and revenue are optional.
const CRITERIA_GROUPS: { key: string; label: string }[] = [
  { key: "segment", label: "Industry segment" },
  { key: "location", label: "Country / Port" },
  { key: "revenue", label: "Revenue" },
];

const isRevenueBand = (part: string) => /\bUSD\b/.test(part);

// "HS 39, 85" is the HS-code fallback, which is a segment even when it stands alone.
const isHsFallback = (part: string) => /^HS\s/.test(part);

const getCriteriaParts = (criteria: string): Record<string, string> => {
  const parts = criteria
    .split("|")
    .map((part) => part.trim())
    .filter((part) => part && part !== "-");
  const result: Record<string, string> = {};
  const nonRevenue = parts.filter((part) => !isRevenueBand(part));
  // Without onboarding segments the backend drops the segment, so a lone part is the country or port.
  if (nonRevenue.length === 1 && !isHsFallback(nonRevenue[0])) {
    result.location = nonRevenue[0];
  } else {
    if (nonRevenue[0]) result.segment = nonRevenue[0];
    if (nonRevenue[1]) result.location = nonRevenue[1];
  }
  const revenue = parts.find(isRevenueBand);
  if (revenue) result.revenue = revenue;
  return result;
};

export const buildCriteriaGroups = (rows: RecommendationRow[]): CriteriaGroup[] => {
  const optionsByGroup: Record<string, Set<string>> = {};
  rows.forEach((row) => {
    Object.entries(getCriteriaParts(row.criteria)).forEach(([key, value]) => {
      (optionsByGroup[key] ||= new Set()).add(value);
    });
  });
  return CRITERIA_GROUPS.filter(({ key }) => optionsByGroup[key]?.size).map(({ key, label }) => ({ key, label, options: Array.from(optionsByGroup[key]).sort() }));
};

// Keep only the selected options that still exist after the rows change (e.g. after a dismiss).
export const pruneCriteriaSelection = (selection: CriteriaSelection, groups: CriteriaGroup[]): CriteriaSelection => {
  const optionsByGroup = new Map(groups.map(({ key, options }) => [key, new Set(options)]));
  return Object.fromEntries(Object.entries(selection).map(([key, values]) => [key, values.filter((value) => optionsByGroup.get(key)?.has(value))]));
};

// OR within a group, AND across groups.
export const matchesCriteria = (row: RecommendationRow, selection: CriteriaSelection) => {
  const parts = getCriteriaParts(row.criteria);
  return Object.entries(selection).every(([key, values]) => values.length === 0 || values.includes(parts[key]));
};
